import { ErrorMessage } from "@hookform/error-message";
import { Card, Grid } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Editor } from "@tinymce/tinymce-react";
import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiSelect from "components/SuiSelect";
import SuiTypography from "components/SuiTypography";
import { AccountContext } from "context/account";
import { nanoid } from "nanoid";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { beforeUpload, dataURLtoFile, getBase64 } from "utils/imageHelper";

const BLOG_TITLE_MAX_LENGTH = 100;
const BLOG_SUMMARY_MAX_LENGTH = 200;

export const blogLabelConfigs = [
  { label: "Design", value: "Design" },
  { label: "Manufacturing", value: "Manufacturing" },
  { label: "Resources", value: "Resources" },
  { label: "Tech Pack", value: "Tech Pack" },
  { label: "Moodboard", value: "Moodboard" },
  { label: "Collection", value: "Collection" },
  { label: "Style", value: "Style" },
  { label: "Collaboration", value: "Collaboration" },
  { label: "Sample", value: "Sample" },
  { label: "Wholesale", value: "Wholesale" },
  { label: "Marketing", value: "Marketing" },
  { label: "AI Fashion", value: "AI Fashion" },
  { label: "Fashion Calendar", value: "Fashion Calendar" },
  { label: "Portfolio", value: "Portfolio" },
  { label: "Buyers", value: "Buyers" },
  { label: "Specialists", value: "Specialists" },
  { label: "Sourcing", value: "Sourcing" },
];

//https://www.tiny.cloud/docs/demo/full-featured/#
const BlogEditor = ({ blogid }) => {
  const [blog, setBlog] = useState(null);
  const [blogTitle, setBlogTitle] = useState();
  const [blogSummary, setBlogSummary] = useState();
  const [mainImageUrl, setMainImageUrl] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [blogContent, setBlogContent] = useState("");

  const account = useContext(AccountContext);
  if (blog && blog.owner_id != account.id) return <></>;

  const loadblog = () => {
    if (blogid) {
      try {
        api.blog.get(blogid).then(({ data }) => {
          // console.log('data', data)
          setBlog(data.blog);
          setBlogTitle(data.blog.title);
          setBlogSummary(data.blog.summary);
          setBlogContent(data.blog.content);
          setSelectedLabels(
            data.blog.labels.map((x) => {
              return { label: x, value: x };
            })
          );
          setMainImageUrl(data.blog.mainimg);
          reset({ title: data.blog.title, summary: data.blog.summary });
          return data;
        });
      } catch (err) {
        toast.error(`get blog failed!`);
        console.log(err);
      }
    }
  };

  useEffect(() => {
    loadblog();
  }, [blogid]);

  let navigate = useNavigate();
  const handleCreateBlog = async (data) => {
    var imageUrl = mainImageUrl;
    if (imageUrl?.startsWith("data")) {
      const file = dataURLtoFile(imageUrl, nanoid());
      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      imageUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }
    var blogobj = {
      title: blogTitle,
      summary: blogSummary,
      labels: selectedLabels.map((x) => x.label),
      mainimg: imageUrl,
      content: blogContent,
    };
    console.log("blogobj", blogobj);
    try {
      if (blog) {
        blogobj.id = blog.id;
        api.blog.update(blogobj).then(({ data }) => {
          navigate(`/social/blog?viewblogid=${data.id}`, { replace: true });
          return data;
        });
      } else {
        api.blog.create(blogobj).then(({ data }) => {
          navigate(`/social/blog?viewblogid=${data.id}`, { replace: true });
          return data;
        });
      }
    } catch (err) {
      toast.error(`blog create failed!`);
      console.log(err);
    }
  };

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  var edit = (blogid ?? "") != "";
  const editorRef = useRef(null);
  var useDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;

  const handleEditorChange = (content, editor) => {
    setBlogContent(content);
    //console.log('Content was updated:', content);
  };

  const handleUploadImages = (data) => {
    return getBase64(data.file);
    // return api.uploads.create([file])
  };

  const handleCropImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      setAvatarImageUrl(url);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCropBackgroundImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      setMainImageUrl(url);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <SuiBox p={2}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} lg={11} xl={10} xxl={9}>
          <form onSubmit={handleSubmit(handleCreateBlog)}>
            <div className="pb-2">
              <Card sx={{ p: 2 }}>
                <SuiTypography variant="h6" fontWeight="medium">
                  {edit ? "Edit Blog" : "New Blog"}
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" color="text">
                  {edit ? "You may modify your blog here." : "You may start new blog here."}
                </SuiTypography>
                <Grid container justifyContent="center" mt={1}>
                  <Grid item xs={12} sm={12} md={12}>
                    <SuiBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                        <SuiTypography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          required
                        >
                          Blog Title:
                        </SuiTypography>
                      </SuiBox>
                      <SuiInput
                        componentsProps={{ input: { maxlength: BLOG_TITLE_MAX_LENGTH } }}
                        {...register("title", {
                          required: "A blog title is required!",
                          maxLength: {
                            value: BLOG_TITLE_MAX_LENGTH,
                            message: `Maxmium length should not exceed ${BLOG_TITLE_MAX_LENGTH} characters`,
                          },
                          onChange: (e) => {
                            setBlogTitle(e.target.value);
                          },
                        })}
                        value={blogTitle}
                        placeholder="blog title desc here."
                      />
                      <SuiBox display="flex" justifyContent="space-between">
                        <div>
                          <ErrorMessage
                            errors={errors}
                            name="title"
                            render={({ message }) => (
                              <SuiTypography
                                variant="text"
                                fontWeight="regular"
                                fontSize="12px"
                                color="error"
                                className="ml-2"
                              >
                                {message}
                              </SuiTypography>
                            )}
                          />
                        </div>
                        <SuiTypography
                          variant="text"
                          color="secondary"
                          fontWeight="regular"
                          fontSize="14px"
                        >
                          {blogTitle ? blogTitle.length : 0}/{BLOG_TITLE_MAX_LENGTH}
                        </SuiTypography>
                      </SuiBox>
                    </SuiBox>
                  </Grid>
                </Grid>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} mt={0} lineHeight={0} display="inline-block">
                    <SuiTypography component="label" variant="caption" fontWeight="bold">
                      Blog Summary:
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    componentsProps={{ input: { maxlength: BLOG_SUMMARY_MAX_LENGTH } }}
                    {...register("summary", {
                      maxLength: {
                        value: BLOG_SUMMARY_MAX_LENGTH,
                        message: `Maxmium length should not exceed ${BLOG_SUMMARY_MAX_LENGTH} characters`,
                      },
                      onChange: (e) => {
                        setBlogSummary(e.target.value);
                      },
                    })}
                    value={blogSummary}
                    placeholder="blog summary here."
                  />
                  <SuiBox display="flex" justifyContent="space-between">
                    <div>
                      <ErrorMessage
                        errors={errors}
                        name="summary"
                        render={({ message }) => (
                          <SuiTypography
                            variant="text"
                            fontWeight="regular"
                            fontSize="12px"
                            color="error"
                            className="ml-2"
                          >
                            {message}
                          </SuiTypography>
                        )}
                      />
                    </div>
                    <SuiTypography
                      variant="text"
                      color="secondary"
                      fontWeight="regular"
                      fontSize="14px"
                    >
                      {blogSummary ? blogSummary.length : 0}/{BLOG_SUMMARY_MAX_LENGTH}
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Labels:
                      </SuiTypography>
                    </SuiBox>
                    <div className="flex items-center">
                      <div className="grow">
                        <Tooltip title={`You can select at most 5 labels!`} placement="top">
                          <Controller
                            name="lables"
                            control={control}
                            render={({ field }) => {
                              return (
                                <SuiSelect
                                  isMulti
                                  closeMenuOnSelect={false}
                                  {...field}
                                  onChange={(options) => {
                                    setSelectedLabels(options);
                                  }}
                                  options={blogLabelConfigs}
                                  value={selectedLabels}
                                />
                              );
                            }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} mt={1.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Main Image:
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox display="flex" alignItems="center" justifyContent="center">
                      <SuiBox
                        className="aspect-[3/2] max-w-lg"
                        display="flex"
                        alignItems="center"
                        position="center"
                        minHeight="18.75rem"
                        borderRadius="xl"
                        sx={{
                          backgroundImage: mainImageUrl
                            ? `url(${mainImageUrl})`
                            : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                                `${linearGradient(
                                  rgba(gradients.secondary.main, 0.2),
                                  rgba(gradients.secondary.state, 0.4)
                                )}`,
                          backgroundSize: "cover",
                          backgroundPosition: "50%",
                          overflow: "hidden",
                        }}
                      />
                    </SuiBox>
                  </SuiBox>
                  <div className="flex items-center mt-2 justify-center">
                    <div className="flex gap-1">
                      <ImgCrop
                        rotate
                        shape="rect"
                        aspect={1.5}
                        minZoom={0.5}
                        maxZoom={3}
                        onModalOk={handleCropBackgroundImgOk}
                      >
                        <Upload
                          name="avatar"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          beforeUpload={beforeUpload}
                        >
                          <div className="text-center h-full flex">
                            <SuiButton variant="gradient" color="info">
                              Upload
                            </SuiButton>
                          </div>
                        </Upload>
                      </ImgCrop>
                    </div>
                  </div>
                </Grid>
                <SuiBox mb={1} ml={0.5} mt={1} lineHeight={0} display="inline-block">
                  <SuiTypography
                    required
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Blog Content:
                  </SuiTypography>
                </SuiBox>
                <input
                  id="uploadfile"
                  type="file"
                  name="uploadfile"
                  style={{ display: "none" }}
                  onclick="this.value=null;"
                  onChange=""
                />
                <Editor
                  apiKey="gyml00g4lb1muvv9cai1t3hr8raf55cz3w6wdblguzvtam9w"
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  initialValue={blog?.content}
                  onEditorChange={handleEditorChange}
                  init={{
                    selector: "textarea#open-source-plugins",
                    plugins:
                      "print preview paste importcss searchreplace autolink autosave directionality code visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons",
                    imagetools_cors_hosts: ["picsum.photos"],
                    menubar: true,
                    statusbar: false,
                    toolbar:
                      "undo redo | fontfamily fontsize bold italic underline strikethrough | forecolor backcolor removeformat pagebreak | alignleft aligncenter alignright alignjustify outdent indent | numlist bullist |fullscreen preview print | image media link template anchor table",
                    toolbar_sticky: true,
                    image_advtab: false,
                    importcss_append: true,
                    file_picker_callback: function (callback, value, meta) {
                      //console.log("value", value, 'meta', meta)
                      var input = document.getElementById("uploadfile");
                      input.value = null;
                      if (meta.filetype === "image") input.accept = "image/*";
                      if (meta.filetype === "media") input.accept = "video/*";
                      if (meta.filetype === "file") input.accept = "";
                      input.click();
                      input.onchange = function () {
                        if (input.files.length == 1) {
                          var file = input.files[0];
                          var file2 = new File([file], nanoid() + "_" + file.name, {
                            type: file.type,
                          });
                          api.uploads.create([file2]).then(({ data }) => {
                            const uploaded = data.uploads.map(({ url }) => url);
                            if (uploaded && uploaded.length == 1)
                              callback(uploaded[0], { alt: file.name, text: file.name });
                            return uploaded;
                          });
                        }
                      };
                    },
                    templates: [
                      {
                        title: "Add Header",
                        description: "Add header which will be added into table of contents.",
                        content: "<hr/><h2>Header</h2><p>Header Context</p>",
                      },
                      {
                        title: "Add Subheader",
                        description: "Add subheader which will be added into table of contents.",
                        content: "<h3>Subheader</h3><p>Subheader Context</p>",
                      },
                    ],
                    template_cdate_format: "[Date Created (CDATE): %m/%d/%Y : %H:%M:%S]",
                    template_mdate_format: "[Date Modified (MDATE): %m/%d/%Y : %H:%M:%S]",
                    height: 800,
                    image_caption: true,
                    contextmenu: false,
                    quickbars_selection_toolbar: "bold italic | quicklink h2 h3 blockquote",
                    noneditable_noneditable_class: "mceNonEditable",
                    toolbar_mode: "sliding",
                    skin: useDarkMode ? "oxide-dark" : "oxide",
                    content_css: useDarkMode ? "dark" : "default",
                    fontsize_formats: "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt",
                    // content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:18pt }'
                  }}
                />
                <Card>
                  <SuiBox display="flex" justifyContent="flex-end" mt={3} mb={2} mr={2}>
                    <SuiButton variant="gradient" color="dark" type="submit">
                      {"Save"}
                    </SuiButton>
                  </SuiBox>
                </Card>
              </Card>
            </div>
          </form>
        </Grid>
      </Grid>
    </SuiBox>
  );
};

export default BlogEditor;
